import { isNull, isString } from 'lodash';
import { forkJoin, from, Observable } from 'rxjs';
import { catchError, concatMap, map } from 'rxjs/operators';
import { VOID } from '../../rxjs-utils';

import { SerializableObject } from '../data/serializable-object';
import { CacheRecord } from '../interfaces/cache-record.interface';
import { Dictionary } from '../interfaces/dictionary.interface';
import { Type } from '../interfaces/type.interface';
import { LoggerService, LogLevel } from '../logger/public-api';

import { mobizCreateLocalForageInstance, MobizLocalForage, MobizLocalForageDrivers, MobizLocalForageOptions } from './local-forage';

/**
 * The `CacheServiceBase` is a base class for creating services that can also
 * cache `SerializableObject` instances in the client-side.
 */
export abstract class CacheServiceBase<T extends SerializableObject> {
  /**
   * Changing the schema version will cause all the cached data to be deleted
   * before the next operation. This should be increased when the structure of
   * the cached data has changed.
   *
   * NOTE: All implementations of this base service should clear cache when schema version changes.
   * TODO: Fix IndexedDB bug and re-enable schema version.
   */
  protected abstract get cacheSchemaVersion(): number;

  /**
   * Cache errors are ignored, but the first one needs to be logged.
   */
  private firstErrorLogged: boolean = false;

  /**
   * The maximum cache lifetime, in seconds.
   */
  protected readonly maxCacheLifetime: number = 604800; // 7 * 24 * 60 * 60

  /**
   * The localForage instance.
   */
  protected readonly storageArea: MobizLocalForage;

  /**
   * LocalForage will use the first available driver.
   */
  protected readonly storageDriverPreferences = [
    MobizLocalForageDrivers.WEBSQL,
    MobizLocalForageDrivers.INDEXEDDB,
  ];

  protected constructor(
    protected readonly cacheRecordDataType: Type<T>,
    protected readonly loggerService: LoggerService,
  ) {
    const config: MobizLocalForageOptions = this.getStorageAreaConfig();
    this.storageArea = mobizCreateLocalForageInstance(config);
  }

  /**
   * Default config for the localForage instance.
   */
  protected abstract getStorageAreaConfig(): MobizLocalForageOptions;

  /**
   * Clear all cache records from store.
   */
  public clearCacheRecords(): Observable<void> {
    return from(this.storageArea.clear());
  }

  /**
   * Returns the cached data for the given keys.
   *
   * @param keys The cache keys of the data to retrieve from store.
   */
  protected getCachedData(keys: string[]): Observable<Dictionary<T>> {
    const observableList: Observable<CacheRecord<T> | undefined>[] = [];
    keys.forEach((key: string): void => {
      observableList.push(this.getCacheRecord(key));
    });

    return forkJoin(observableList).pipe(
      concatMap((cacheRecords: (CacheRecord<T> | undefined)[]): Observable<Dictionary<T>> => {
        const cacheData: Dictionary<T> = {};
        cacheRecords.forEach((cacheRecord: CacheRecord<T> | undefined): void => {
          if (cacheRecord) {
            cacheData[cacheRecord.key] = cacheRecord.data;
          }
        });
        return this.validateCachedData(cacheData);
      }),
    );
  }

  /**
   * Returns a cache record that did not expire.
   *
   * @param key The cache key of the data to retrieve from cache.
   */
  protected getCacheRecord(key: string): Observable<CacheRecord<T> | undefined> {
    const processCacheRecord = (cacheRecord: CacheRecord<any>): CacheRecord<T> | undefined => {
      if (isNull(cacheRecord)) {
        return undefined;
      }
      // NOTE: Expired entries will be overridden after fresh data is retrieved from the server.
      if (this.hasExpired(cacheRecord.expire)) {
        return undefined;
      }
      // Properly deserialize the cache record data.
      cacheRecord.data = new this.cacheRecordDataType(cacheRecord.data);
      return cacheRecord;
    };

    return from(this.storageArea.getItem(key)).pipe(
      // Ignore all storage errors.
      catchError((error: any): Observable<void> => {
        if (!this.firstErrorLogged) {
          this.loggerService.log(LogLevel.NOTICE, error.name, {exception: error});
          this.firstErrorLogged = true;
        }
        return VOID;
      }),
      map(processCacheRecord),
    );
  }

  /**
   * Checks if the given cache record date has expired.
   *
   * @param expire The expire date or date ISO string.
   */
  protected hasExpired(expire: Date | string): boolean {
    const expireDate: Date = isString(expire) ? new Date(expire) : expire as Date;
    return expireDate.getTime() < Date.now();
  }

  /**
   * Cache the supplied data.
   *
   * @param key The cache key of the data to cache.
   * @param expire Indicates that the record will be considered invalid after
   *        this time, i.e. it will not be returned by getCacheRecord().
   * @param data The data to store in the cache.
   */
  public setCacheRecord(key: string, expire: Date, data: T): Observable<T | null> {
    const newCacheRecord: CacheRecord<T> = {
      data: data,
      expire: expire.toISOString(),
      key: key,
    };
    return from(this.storageArea.setItem(key, newCacheRecord)).pipe(
      // Ignore all storage errors.
      catchError((error: any): Observable<void> => {
        if (!this.firstErrorLogged) {
          this.loggerService.log(LogLevel.NOTICE, error.name, {exception: error});
          this.firstErrorLogged = true;
        }
        return VOID;
      }),
      // Return the original data.
      map(() => {
        return data;
      }),
    );
  }

  /**
   * Remove a cache record by key.
   *
   * @param key The cache key of the data to remove from cache.
   */
  public removeCacheRecord(key: string): Observable<void> {
    return from(this.storageArea.removeItem(key)).pipe(
      // Ignore all storage errors.
      catchError((error: any): Observable<void> => {
        if (!this.firstErrorLogged) {
          this.loggerService.log(LogLevel.NOTICE, error.name, {exception: error});
          this.firstErrorLogged = true;
        }
        return VOID;
      }),
    );
  }

  /**
   * Remove multiple cache records by key.
   *
   * @param keys The list of cache keys of the data to remove from cache.
   */
  public removeCacheRecords(keys: string[]): Observable<void> {
    const observableList: Observable<void>[] = [];
    keys.forEach((key: string) => {
      observableList.push(this.removeCacheRecord(key));
    });

    return forkJoin(observableList).pipe(
      map((): void => { }),
    );
  }

  /**
   * Perform validation on the returned cached data.
   */
  protected abstract validateCachedData(cachedData: Dictionary<T>): Observable<Dictionary<T>>;
}
